import { useCanWriteWorkspaceAsset } from '@vizit/access';
import type { AssetClass, ImageView } from '@vizit/api';
import { useAssetFavorites, useSetAssetFavoriteMutation } from '@vizit/api';
import { useShowFavorites } from '@vizit/feature-flags';
import { HeartIcon } from '@vizit/icons';
import { Asset, LibraryEmptyState } from '@vizit/molecules';
import { AssetGrid, RemoveImageFromCollectionDialog } from '@vizit/organisms';
import * as React from 'react';
import { CollectionBulkActionBar } from './collection-asset-grid.bulk-action-bar';
import { useCollectionAssetGrid } from './collection-asset-grid.hooks';
import { useRemoveAssetFromCollection } from './collection-asset-grid.remove-asset.hook';
import { useAssetSelection } from './collection-asset-grid.selection';
import { CollectionAsset } from './collection-asset-grid.sortable-asset';
import { DEFAULT_ASSET_SORT } from './collection-asset-grid.state';
import type { CollectionAssetSortOption, CollectionImageScoreField } from './collection-asset-grid.state';

interface CollectionAssetGridProps {
  /**
   * The ID of the portfolio
   */
  workspaceId: string;
  /**
   * The ID of the collection
   */
  collectionId: string;
  /**
   * Additional CSS classes to apply to the grid container
   */
  className?: string;
  /**
   * Callback when an asset is clicked
   */
  onNavigateToAsset: (id: string) => void;
  /**
   * Callback when total count changes (useful for displaying count in parent)
   * @param count - The total number of assets in the collection
   */
  onTotalCountChange?: (count: number) => void;
  /**
   * Whether to automatically calculate optimal initial load count
   * @default true
   */
  useOptimalInitialLoad?: boolean;
  /**
   * Number of items to load per page
   * @default 12
   */
  limit?: number;
  /**
   * How to sort assets within the collection
   * @default CollectionAssetSortOption.DateNewest
   */
  sortOption?: CollectionAssetSortOption;
  /**
   * Which score field score-sort options use (e.g. `vizit_score` or `hero_score`)
   * @default 'vizit_score'
   */
  scoreField?: CollectionImageScoreField;
  /**
   * When true, generated (Spark) images are hidden
   * @default false
   */
  hideSparkImages?: boolean;
  /**
   * When true, only favorited assets are shown. Applied client-side against the served
   * `favorite` flag: the collection images endpoint has no `favorite` query param.
   * @default false
   */
  favoritesOnly?: boolean;
  /**
   * When set, only images of this asset class are shown; `undefined` shows all classes
   */
  classification?: AssetClass;
  /**
   * Fixed images per row, e.g. from the thumbnail size slider. `undefined` keeps the responsive columns
   */
  columns?: number;
  /**
   * Multi-select with checkboxes and the action bar (BLU-348). Selection starts over when the collection, filters or
   * sort change (SDD D59)
   * @default false
   */
  isSelectable?: boolean;
  /**
   * Called when the selection starts or ends, so the page can make room for the action bar (e.g. hide the thumbnail
   * slider that sits in the same bottom area)
   */
  onSelectionModeChange?: (isSelectionMode: boolean) => void;
}

/**
 * CollectionAssetGrid component that fetches and displays assets within a collection
 * Uses infinite loading pattern for better performance
 *
 * @param props - The component props
 */
const CollectionAssetGrid = React.memo(function CollectionAssetGrid(props: CollectionAssetGridProps) {
  const {
    workspaceId,
    collectionId,
    className,
    onNavigateToAsset,
    onTotalCountChange,
    useOptimalInitialLoad = true,
    limit = 12,
    sortOption = DEFAULT_ASSET_SORT,
    scoreField,
    hideSparkImages = false,
    favoritesOnly: favoritesOnlyProp = false,
    classification,
    columns,
    isSelectable = false,
    onSelectionModeChange,
  } = props;

  const { assets, totalCount, isLoading, hasNextPage, isFetchingNextPage, sentinelRef, loadingCount } =
    useCollectionAssetGrid({
      workspaceId,
      collectionId,
      limit,
      useOptimalInitialLoad,
      sortOption,
      scoreField,
      hideSparkImages,
      classification,
      columns,
    });

  const { isFavorite } = useAssetFavorites();
  const { mutate: setFavorite } = useSetAssetFavoriteMutation();
  const canFavorite = useCanWriteWorkspaceAsset();
  const showFavorites = useShowFavorites();
  // With favourites off, a bookmarked `?fav=only` would filter the grid to
  // nothing with no toggle left to clear it. Neutralize it here so every
  // derived value below — visibleAssets, visibleCount, the scanning and
  // empty-filter states — falls out correct.
  const favoritesOnly = showFavorites ? favoritesOnlyProp : false;

  // A write is addressed at the asset's own workspace, not the one it was
  // reached through -- the backend authorizes on the row's workspace and 404s
  // otherwise. Older payloads may omit it, so fall back to the route's.
  const favoriteWorkspaceId = React.useCallback((asset: ImageView) => asset.workspace_id ?? workspaceId, [workspaceId]);

  const handleToggleFavorite = React.useCallback(
    (asset: ImageView) => {
      const assetWorkspaceId = favoriteWorkspaceId(asset);
      if (!assetWorkspaceId) return;
      setFavorite({ workspace_id: assetWorkspaceId, image_id: asset.id, is_favorite: !isFavorite(asset) });
    },
    [setFavorite, favoriteWorkspaceId, isFavorite],
  );

  const visibleAssets = React.useMemo(
    () => (favoritesOnly ? assets.filter((asset: ImageView) => isFavorite(asset)) : assets),
    [assets, favoritesOnly, isFavorite],
  );

  // "Select visible" alone does nothing with the selection, so checkboxes and the bar show only when the bar has a
  // real action. Delete (BLU-353) and Download (BLU-351) join Favorite here
  const hasBulkActions = showFavorites;
  const isSelectionEnabled = isSelectable && hasBulkActions;

  const visibleIds = React.useMemo(() => visibleAssets.map((asset: ImageView) => asset.id), [visibleAssets]);
  const selection = useAssetSelection({
    orderedIds: visibleIds,
    resetKey: [collectionId, sortOption, scoreField, hideSparkImages, favoritesOnly, classification].join('|'),
  });
  const selectedAssets = React.useMemo(
    () => visibleAssets.filter((asset: ImageView) => selection.selectedIds.has(asset.id)),
    [visibleAssets, selection.selectedIds],
  );
  const isSelectionMode = selectedAssets.length > 0;

  React.useEffect(() => {
    onSelectionModeChange?.(isSelectionMode);
  }, [isSelectionMode, onSelectionModeChange]);

  const { isOpen, imageIdToRemove, onOpenChange, onRemoveImageDialogOpen, onRemove } = useRemoveAssetFromCollection({
    workspaceId,
    collectionId,
  });

  // With the favorites filter on the server's total describes the unfiltered
  // collection, so it would caption a grid of two tiles "12 assets". Only swap
  // in the filtered count once every page has arrived: the filter runs over
  // fetched pages, so counting mid-scroll would show a number that climbs.
  const visibleCount = favoritesOnly && !hasNextPage ? visibleAssets.length : totalCount;

  React.useEffect(() => {
    onTotalCountChange?.(visibleCount);
  }, [visibleCount, onTotalCountChange]);

  // The filter runs over fetched pages, so an empty filtered view with pages
  // still to come is mid-scan, not a miss: the scroll sentinel keeps pulling
  // until a match appears or the list runs out.
  const isScanningForMatches = favoritesOnly && visibleAssets.length === 0 && hasNextPage;

  // `AssetGrid` renders its "collection is empty, upload something" state
  // whenever it has no children, which would misreport a filter that simply
  // matched nothing. Hold it back until there are no more pages to arrive,
  // mirroring the assets drawer.
  const hasNoFilterMatches = favoritesOnly && visibleAssets.length === 0 && !isLoading && !hasNextPage;

  if (hasNoFilterMatches) {
    return (
      <div className="flex w-full flex-grow items-center justify-center">
        <LibraryEmptyState
          heading="No assets match your filters."
          description="Try turning the favorites filter off, or favorite an asset from its tile."
          icon={HeartIcon}
        />
      </div>
    );
  }

  return (
    <>
      <AssetGrid
        showCount={false}
        loadingCount={loadingCount || limit}
        totalCount={visibleCount}
        isLoading={isLoading || isScanningForMatches}
        className={className}
        columns={columns}
      >
        {visibleAssets.map((asset: ImageView) => (
          <CollectionAsset
            key={asset.id}
            image={asset}
            onAssetClick={onNavigateToAsset}
            onRemoveImageDialogOpen={onRemoveImageDialogOpen}
            isFavorite={isFavorite(asset)}
            isFavoriteDisabled={
              !favoriteWorkspaceId(asset) || !canFavorite({ workspace_id: favoriteWorkspaceId(asset) })
            }
            onToggleFavorite={showFavorites ? () => handleToggleFavorite(asset) : undefined}
            selection={
              isSelectionEnabled
                ? {
                    isSelected: selection.isSelected(asset.id),
                    isSelectionMode,
                    onSelectedChange: ({ shiftKey }) => selection.toggle(asset.id, { shiftKey }),
                  }
                : undefined
            }
          />
        ))}

        {/* Show loading assets when fetching next page */}
        {isFetchingNextPage && loadingCount
          ? Array.from({ length: loadingCount }, (_, index) => {
              const skeletonId = `collection-asset-skeleton-next-${index}`;
              return (
                <Asset key={skeletonId} isLoading showScoreBar>
                  <Asset.Image name="Loading..." src={null} />
                </Asset>
              );
            })
          : null}
      </AssetGrid>

      {/* Sentinel element for infinite scroll detection */}
      {hasNextPage && <div ref={sentinelRef} className="h-4 w-full" aria-label="Loading more assets" />}

      {isSelectionEnabled && (
        <CollectionBulkActionBar
          selectedAssets={selectedAssets}
          visibleCount={visibleIds.length}
          workspaceId={workspaceId}
          onSelectVisible={selection.selectVisible}
          onClear={selection.clear}
          showFavorite={showFavorites}
        />
      )}

      <RemoveImageFromCollectionDialog
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        onRemove={onRemove}
        imageId={imageIdToRemove}
      />
    </>
  );
});

export { CollectionAssetGrid };
export type { CollectionAssetGridProps };
